/**
 * IntroComposition — Remotion composition for animated show intros and outros.
 * Rendered inside a Remotion Player; preset-driven animation.
 */
import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';
import type { TemplateColorPalette } from '../../types';

export interface IntroCompositionProps {
  preset: string;
  channelName: string;
  colorPalette: TemplateColorPalette;
  tagline?: string;
  isOutro?: boolean;
}

export const IntroComposition: React.FC<IntroCompositionProps> = ({
  preset, channelName, colorPalette, tagline = 'News Channel', isOutro = false,
}) => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const fadeOut = interpolate(frame, [durationInFrames - 12, durationInFrames], [1, 0], {
    extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
  });

  const bg: React.CSSProperties = {
    fontFamily: "'Inter', 'Roboto', sans-serif",
  };

  // ── buzz24-classic ──────────────────────────────────────────
  if (preset === 'buzz24-classic') {
    const sc = spring({ frame, fps, config: { damping: 14, stiffness: 110 } });
    const barW = interpolate(frame, [15, 50], [0, 100], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    const subOp = interpolate(frame, [35, 55], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    return (
      <AbsoluteFill style={{ ...bg, background: colorPalette.primary, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: fadeOut }}>
        <div style={{ transform: `scale(${sc})`, textAlign: 'center' }}>
          <div style={{ fontSize: 96, fontWeight: 900, color: colorPalette.accent, letterSpacing: -4 }}>
            {channelName}
          </div>
          <div style={{ width: `${barW}%`, height: 5, background: colorPalette.accent, margin: '18px auto', borderRadius: 2 }} />
          <div style={{ fontSize: 22, color: colorPalette.text, opacity: subOp, letterSpacing: 8, textTransform: 'uppercase' }}>
            {isOutro ? 'धन्यवाद · Thank You' : tagline}
          </div>
        </div>
      </AbsoluteFill>
    );
  }

  // ── slide-in ────────────────────────────────────────────────
  if (preset === 'slide-in') {
    const slideX = interpolate(frame, [0, 28], [isOutro ? 200 : -200, 0], {
      extrapolateLeft: 'clamp', extrapolateRight: 'clamp',
    });
    const op = interpolate(frame, [0, 22], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    const subOp = interpolate(frame, [18, 40], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    return (
      <AbsoluteFill style={{ ...bg, background: colorPalette.background, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: fadeOut }}>
        <div style={{ transform: `translateX(${slideX}px)`, opacity: op, textAlign: 'center' }}>
          <div style={{ fontSize: 88, fontWeight: 900, color: colorPalette.accent }}>{channelName}</div>
        </div>
        <div style={{ opacity: subOp, fontSize: 22, color: colorPalette.text, marginTop: 14, letterSpacing: 4 }}>
          {isOutro ? 'धन्यवाद · Thank You' : tagline}
        </div>
      </AbsoluteFill>
    );
  }

  // ── flash ───────────────────────────────────────────────────
  if (preset === 'flash') {
    const flashOp = frame < 24 ? (frame % 7 < 3 ? 0.9 : 0) : 0;
    const mainOp = interpolate(frame, [22, 40], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    const sc = spring({ frame: Math.max(0, frame - 22), fps, config: { damping: 12 } });
    return (
      <AbsoluteFill style={{ ...bg, background: colorPalette.primary, opacity: fadeOut }}>
        <AbsoluteFill style={{ background: colorPalette.accent, opacity: flashOp }} />
        <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: mainOp }}>
          <div style={{ fontSize: 88, fontWeight: 900, color: colorPalette.text, transform: `scale(${sc})` }}>{channelName}</div>
          <div style={{ fontSize: 20, color: colorPalette.accent, marginTop: 12, letterSpacing: 6, textTransform: 'uppercase' }}>
            {isOutro ? 'धन्यवाद · Thank You' : tagline}
          </div>
        </AbsoluteFill>
      </AbsoluteFill>
    );
  }

  // ── fade-up ─────────────────────────────────────────────────
  if (preset === 'fade-up') {
    const ty = interpolate(frame, [0, 30], [70, 0], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    const op = interpolate(frame, [0, 30], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    const subOp = interpolate(frame, [20, 48], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    return (
      <AbsoluteFill style={{ ...bg, background: colorPalette.background, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: fadeOut }}>
        <div style={{ transform: `translateY(${ty}px)`, opacity: op, textAlign: 'center' }}>
          <div style={{ fontSize: 88, fontWeight: 900, color: colorPalette.accent }}>{channelName}</div>
        </div>
        <div style={{ opacity: subOp, fontSize: 22, color: colorPalette.text, marginTop: 16, letterSpacing: 4 }}>
          {isOutro ? 'धन्यवाद · Thank You' : tagline}
        </div>
      </AbsoluteFill>
    );
  }

  // ── breaking ────────────────────────────────────────────────
  if (preset === 'breaking') {
    const barH = interpolate(frame, [0, 20], [0, 90], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    const textOp = interpolate(frame, [18, 38], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
    return (
      <AbsoluteFill style={{ ...bg, background: colorPalette.primary, opacity: fadeOut }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: barH, background: '#cc0000', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
          <div style={{ fontSize: 32, fontWeight: 900, color: '#fff', letterSpacing: 6, textTransform: 'uppercase', opacity: textOp }}>
            {isOutro ? 'THANK YOU' : 'BREAKING NEWS'}
          </div>
        </div>
        <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: textOp }}>
          <div style={{ fontSize: 88, fontWeight: 900, color: colorPalette.text }}>{channelName}</div>
          <div style={{ fontSize: 22, color: colorPalette.accent, marginTop: 10, letterSpacing: 4 }}>{tagline}</div>
        </AbsoluteFill>
      </AbsoluteFill>
    );
  }

  // ── none / default ───────────────────────────────────────────
  const op = interpolate(frame, [0, 20], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <AbsoluteFill style={{ ...bg, background: colorPalette.primary, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: Math.min(op, fadeOut) }}>
      <div style={{ fontSize: 88, fontWeight: 900, color: colorPalette.accent }}>{channelName}</div>
      <div style={{ fontSize: 22, color: colorPalette.text, marginTop: 14, letterSpacing: 4 }}>
        {isOutro ? 'धन्यवाद · Thank You' : tagline}
      </div>
    </AbsoluteFill>
  );
};
